import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  List,
  BarChart3,
  Shield,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Globe,
  Code,
  Mail,
  Link2,
  Key,
  Users,
  DollarSign,
  Eye,
  Phone,
  Search,
  CreditCard,
  Receipt,
  BookOpen,
  Settings,
  HelpCircle,
  User
} from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/investigate", label: "Investigate", icon: Search },
  { path: "/logs", label: "IP Logs", icon: List },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/blacklist", label: "Blacklist", icon: Shield },
  { path: "/alerts", label: "Alerts", icon: AlertTriangle },
  { path: "/tracking", label: "Tracking Pixel", icon: Code },
  { path: "/email-analyzer", label: "Email Headers", icon: Mail },
  { path: "/link-tracker", label: "Link Tracker", icon: Link2 },
  { path: "/subscription", label: "Subscription", icon: CreditCard },
  { path: "/invoices", label: "Invoices", icon: Receipt },
];

const adminItems = [
  { path: "/admin", label: "License Admin", icon: Key },
  { path: "/admin/licenses", label: "Licenses", icon: Key },
  { path: "/admin/affiliates", label: "Affiliates", icon: Users },
  { path: "/admin/payouts", label: "Payouts", icon: DollarSign },
];

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background grid-pattern" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full bg-card border-r border-border z-50 transition-all duration-300 ${
          sidebarOpen ? "w-60" : "w-16"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Globe className="w-7 h-7 text-primary" />
              <span className="font-mono font-bold text-lg tracking-tight">NetID</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-1" data-testid="nav-menu">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200
                ${isActive 
                  ? "bg-primary/10 text-primary border border-primary/20" 
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }
                ${!sidebarOpen && "justify-center"}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          ))}
          
          {/* Admin Section */}
          {sidebarOpen && (
            <div className="pt-4 mt-4 border-t border-border">
              <p className="px-3 text-xs text-muted-foreground uppercase tracking-wider mb-2">Admin</p>
            </div>
          )}
          {adminItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200
                ${isActive 
                  ? "bg-amber-500/10 text-amber-500 border border-amber-500/20" 
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }
                ${!sidebarOpen && "justify-center"}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Logged in as</div>
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-muted-foreground hover:text-destructive"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="w-full text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn-collapsed"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? "lg:ml-60" : "lg:ml-16"}`}>
        {/* Header */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30 flex items-center px-4 lg:px-6" data-testid="header">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-2"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          {/* Logo and Welcome Message */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-primary">
              <Globe className="w-6 h-6" />
              <span className="font-mono font-bold text-lg tracking-tight hidden sm:inline">NetID</span>
            </div>
            <div className="h-6 w-px bg-border hidden sm:block" />
            <div className="hidden sm:block">
              <span className="text-sm text-muted-foreground">Welcome, </span>
              <span className="text-sm font-semibold text-foreground">{user?.name || 'User'}</span>
            </div>
          </div>
          
          <div className="flex-1" />
          
          {/* User Menu Dropdown */}
          <div className="flex items-center gap-4">
            <div className="text-xs text-muted-foreground font-mono hidden md:block">
              {new Date().toLocaleString()}
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.name?.split(' ')[0] || 'Menu'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/guide')} className="cursor-pointer">
                  <BookOpen className="w-4 h-4 mr-2" />
                  User Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/subscription')} className="cursor-pointer">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/invoices')} className="cursor-pointer">
                  <Receipt className="w-4 h-4 mr-2" />
                  Invoices
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout} 
                  className="cursor-pointer text-red-500 focus:text-red-500"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
